// HEAL-46a §6.4 (решение D-18) — строка-вход к непроверенным анализам.
//
// До этой версии непроверенный анализ подменял Главную целиком, и строка была не
// нужна: человек и так стоял на проверке. Теперь Главная остаётся Главной, а
// напоминание живёт здесь — и это единственное, что связывает человека с
// незаконченным разбором.
//
// Поэтому строка обязана быть заметной, но не перекрывать содержимое: её задача
// — напомнить и увести, а не отобрать экран обратно.

import { ChevronRight, Clock, FileCheck2 } from 'lucide-react';
import { useLocation } from 'wouter';

type Props = {
  /** Сколько анализов ждёт проверки. Ноль — строка не показывается. */
  awaitingCount: number;
  /** Сколько ещё обрабатывается. Показываем отдельно: это не требует действий. */
  processingCount?: number;
  /** Куда вести: пакет, если разбор идёт пакетом, иначе конкретный анализ. */
  href: string;
  /** Побочные действия перехода — например, запомнить, откуда человек ушёл. */
  onNavigate?: () => void;
};

export function PendingReviewBar({ awaitingCount, processingCount = 0, href, onNavigate }: Props) {
  const [, setLocation] = useLocation();

  if (awaitingCount === 0 && processingCount === 0) return null;

  // Пока идёт обработка, человеку делать нечего — сообщаем факт, не зовём к
  // действию. Как только появились готовые, тон меняется на побуждающий.
  const waiting = awaitingCount === 0;

  const label = waiting
    ? `${processingCount} ${plural(processingCount, 'анализ обрабатывается', 'анализа обрабатываются', 'анализов обрабатываются')}`
    : `${awaitingCount} ${plural(awaitingCount, 'анализ ждёт проверки', 'анализа ждут проверки', 'анализов ждут проверки')}`;

  return (
    <button
      type="button"
      onClick={() => {
        onNavigate?.();
        setLocation(href);
      }}
      className={[
        'flex w-full items-center gap-3 rounded-surface border px-4 py-3 text-left transition',
        waiting
          ? 'border-border bg-muted text-gray-700'
          : 'border-amber-200 bg-amber-50 text-amber-900',
      ].join(' ')}
    >
      {waiting ? (
        <Clock className="h-5 w-5 shrink-0 text-gray-400" aria-hidden="true" />
      ) : (
        <FileCheck2 className="h-5 w-5 shrink-0 text-amber-600" aria-hidden="true" />
      )}
      <span className="min-w-0 flex-1 text-sm font-medium">{label}</span>
      <ChevronRight className="h-4 w-4 shrink-0 opacity-60" aria-hidden="true" />
    </button>
  );
}

/** Русская форма числительного: 1 анализ, 2 анализа, 5 анализов. */
export function plural(n: number, one: string, few: string, many: string): string {
  const mod100 = n % 100;
  if (mod100 >= 11 && mod100 <= 14) return many;

  switch (n % 10) {
    case 1:
      return one;
    case 2:
    case 3:
    case 4:
      return few;
    default:
      return many;
  }
}
